import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { AlertCircle, Sparkles, Shield, Users } from "lucide-react";
import { isAuthenticated, getCurrentUser, loginWithGoogle } from "../lib/auth.js";

// Login stránka — Google Sign-In přes Google Identity Services (GSI).
// Po úspěšném přihlášení smart-redirect podle role:
//   owner / manager / sales / csm / marketing → /dashboard
//   trener → /trener/dashboard
export default function Login() {
  const buttonContainerRef = useRef(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(isAuthenticated());

  useEffect(() => {
    if (session) return; // prihlaseny user se rovnou presmeruje, GSI netreba
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setError("Chybí VITE_GOOGLE_CLIENT_ID env var. Owner musí nastavit Google OAuth Client ID v Netlify env vars.");
      return;
    }
    let attempts = 0;
    const init = () => {
      if (!window.google?.accounts?.id) {
        attempts++;
        if (attempts < 50) return setTimeout(init, 100);
        setError("Google Sign-In SDK se nenačetlo. Zkontroluj internetové připojení nebo blokátory reklam.");
        return;
      }
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
        auto_select: false,
        context: "signin",
        ux_mode: "popup",
      });
      if (buttonContainerRef.current) {
        // Mobile-friendly width: GSI max je 400, ale na úzkém viewportu
        // 400px + container padding přetekal. Spočítáme tak aby se vlezl do karty
        // s p-5 (mobile) / p-8 (sm) / p-10 (md) paddingem + 16px page padding.
        const vw = window.innerWidth;
        const horizPad = vw < 640 ? 20 + 16 : 40 + 16; // card padding + page padding (jen jedna strana, druhá symetrie)
        const buttonWidth = Math.min(400, vw - horizPad * 2);
        window.google.accounts.id.renderButton(buttonContainerRef.current, {
          type: "standard",
          theme: "filled_blue",
          size: "large",
          text: "signin_with",
          shape: "pill",
          logo_alignment: "left",
          width: buttonWidth,
          locale: "cs",
        });
      }
    };
    init();
  }, [session]);

  const handleCredentialResponse = async (response) => {
    setLoading(true);
    setError("");
    try {
      const sessionData = await loginWithGoogle(response.credential);
      // Hard navigace přes window.location aby se celý JS bundle načetl čistě
      // (React Navigate někdy způsobí prázdnou obrazovku, protože Layout/Dashboard
      // se nestihly inicializovat před přesměrováním).
      const dest = sessionData?.role === "trener" ? "/trener/dashboard" : "/dashboard";
      window.location.href = dest;
    } catch (e) {
      setError(e.message || "Přihlášení selhalo");
      setLoading(false);
    }
  };

  // Smart redirect po uspesnem loginu — trener rovnou do sveho portalu.
  // 6.8.2026 fix React #310: guard musi byt AZ za vsemi hooky.
  if (session) {
    const user = getCurrentUser();
    const dest = user?.role === "trener" ? "/trener/dashboard" : "/dashboard";
    return <Navigate to={dest} replace />;
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#241430] flex items-center justify-center p-3 sm:p-4" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      {/* Plynulá animace plujících světel + vstupní fade. */}
      <style>{`
        @keyframes fw-float-a { 0%,100%{transform:translate(-50%,0) scale(1)} 50%{transform:translate(-50%,-40px) scale(1.08)} }
        @keyframes fw-float-b { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-30px,30px) scale(1.1)} }
        @keyframes fw-float-c { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(40px,-20px) scale(1.05)} }
        @keyframes fw-rise { from{opacity:0; transform:translateY(16px)} to{opacity:1; transform:translateY(0)} }
        .fw-rise{ animation: fw-rise .6s cubic-bezier(.22,1,.36,1) both; }
        @media (prefers-reduced-motion: reduce){ .fw-float-a,.fw-float-b,.fw-float-c,.fw-rise{ animation:none !important; } }
      `}</style>

      {/* Animované gradient pozadí */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#3a2154] via-[#241430] to-[#1b0f26]" />
      <div className="fw-float-a absolute top-0 left-1/2 w-[800px] h-[800px] rounded-full bg-[#7C5DB0]/35 blur-3xl pointer-events-none" style={{ animation: "fw-float-a 14s ease-in-out infinite" }} />
      <div className="fw-float-b absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full bg-[#9B7FC7]/25 blur-3xl pointer-events-none" style={{ animation: "fw-float-b 18s ease-in-out infinite" }} />
      <div className="fw-float-c absolute top-1/3 left-0 w-[500px] h-[500px] rounded-full bg-[#7C5DB0]/15 blur-3xl pointer-events-none" style={{ animation: "fw-float-c 16s ease-in-out infinite" }} />

      {/* Subtle grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: "linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Hlavní karta */}
      <div className="relative z-10 w-full max-w-xl mx-auto fw-rise">
        {/* Brand nad kartou - mobile-first: menší FW + menší titulek aby nepřetekly */}
        <div className="text-center mb-5 sm:mb-7">
          <div className="inline-flex items-center gap-3 sm:gap-4 mb-3">
            <img
              src="/ikona-bila.svg"
              alt=""
              className="w-12 h-12 sm:w-16 sm:h-16 md:w-[72px] md:h-[72px] object-contain"
            />
            <div className="text-left">
              <div className="text-xl sm:text-3xl md:text-4xl font-serif font-semibold text-white leading-tight">
                FitWay<span className="text-[#B9A0E0]">40</span> <span className="font-sans font-medium text-white/80">Admin</span>
              </div>
              <div className="text-[11px] sm:text-sm text-[#C9B6E8]/90 font-medium uppercase tracking-wider">Interní systém týmu FitWay40</div>
            </div>
          </div>
        </div>

        {/* Karta s loginem - menší padding na mobile (jinak Google button přeteče) */}
        <div className="bg-white/[0.97] backdrop-blur-xl rounded-3xl shadow-2xl shadow-black/40 p-5 sm:p-8 md:p-10 ring-1 ring-white/10">
          <div className="text-center mb-5 sm:mb-7">
            <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#EFE8FA] text-[#5b4390] text-xs font-semibold mb-3">
              <Shield className="w-3.5 h-3.5" /> Pouze pro členy týmu FitWay40
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold text-[#241430]">Vítej zpět</h1>
            <p className="text-sm sm:text-base md:text-lg text-[#241430]/70 mt-2 sm:mt-2.5">Přihlas se svým Google účtem a pokračuj do aplikace</p>
          </div>

          {error && (
            <div className="mb-5 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-xl text-sm sm:text-base text-red-700 flex items-start gap-2.5">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {loading && (
            <div className="mb-5 p-3 sm:p-4 bg-[#EFE8FA] border border-[#7C5DB0]/30 rounded-xl text-sm sm:text-base text-[#5b4390] text-center font-medium flex items-center justify-center gap-2.5">
              <div className="w-5 h-5 border-2 border-[#7C5DB0] border-t-transparent rounded-full animate-spin" />
              Ověřuji a otevírám aplikaci…
            </div>
          )}

          {/* Google tlačítko - scale jen na desktop (mobile musí v 1:1 jinak přeteče) */}
          <div className="flex justify-center overflow-hidden py-2">
            <div
              ref={buttonContainerRef}
              className="flex justify-center sm:scale-[1.18]"
              style={{ transformOrigin: "center", filter: "hue-rotate(47deg) saturate(0.75)" }}
            />
          </div>

          {/* Feature ikonky pod tlačítkem - menší gap a padding na mobile */}
          <div className="mt-6 sm:mt-9 pt-5 sm:pt-7 border-t border-[#7C5DB0]/10 grid grid-cols-3 gap-2 sm:gap-3 text-center">
            <FeatureBadge icon={Shield} label="Bezpečné" sub="OAuth 2.0" />
            <FeatureBadge icon={Sparkles} label="Bez hesla" sub="Google login" />
            <FeatureBadge icon={Users} label="Multi-role" sub="owner, trenér" />
          </div>
        </div>

        {/* Footer info - menší text a wrap-friendly na mobile */}
        <div className="mt-5 sm:mt-7 text-center text-xs sm:text-sm text-[#C9B6E8]/85 space-y-1.5 px-2">
          <div className="break-words">
            Jsi klientka? Tvoje přihlášení je v{" "}
            <a href="https://aplikace.fitway40.cz/" className="font-semibold text-white underline hover:text-[#C9B6E8]">klientské aplikaci</a>
            . Veřejný web je na <a href="https://www.fitway40.cz/" className="underline hover:text-white">fitway40.cz</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// Drobná feature ikonka s labelem pod login tlačítkem
function FeatureBadge({ icon: Icon, label, sub }) {
  return (
    <div className="flex flex-col items-center gap-1 sm:gap-1.5">
      <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-[#EFE8FA] flex items-center justify-center">
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#7C5DB0]" />
      </div>
      <div className="text-xs sm:text-sm font-bold text-[#241430] leading-tight">{label}</div>
      <div className="text-[11px] sm:text-xs text-[#241430]/50 leading-tight">{sub}</div>
    </div>
  );
}
